import * as Sentry from '@sentry/browser';
import { defaultGlitchTipDSN } from '../../../services/corrections-api/src/diagnostics/settings.ts';
import { sanitizeEvent, sanitizeLog, scrubDiagnostic } from '../../../services/corrections-api/src/diagnostics/sanitization.ts';

const launchID = crypto.randomUUID();
// PostHog's SDK retries its own failed relay requests, and an analytics upstream timeout is no site failure.
// A failed consent lookup already falls back to asking.
const ownOrigin = new RegExp(`^${location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/(?!relay(?:/|$)|v2/t/geo$)`);

// Browser extensions inject their own styles and scripts into every page they run on.
// A CSP report may name one by its scheme alone: Chrome's sourceFile can be the bare word `chrome-extension`.
const extensionSchemes = new Set(['chrome-extension:', 'moz-extension:', 'safari-web-extension:', 'safari-extension:']);
const isBrowserExtensionSource = (url) => {
  if (extensionSchemes.has(`${url}:`)) return true;
  try { return extensionSchemes.has(new URL(url).protocol); } catch { return false; }
};
// An exception whose located frames all belong to an extension (a wallet redefining window.ethereum, say) is the
// extension's failure, not the site's; frames without a URL, such as `<anonymous>`, say nothing either way.
const isBrowserExtensionException = (event) => {
  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];
  const located = frames.filter((frame) => /^[a-z][a-z\d+.-]*:\/\//i.test(frame.filename ?? ''));
  return located.length > 0 && located.every((frame) => isBrowserExtensionSource(frame.filename));
};

Sentry.init({
  dsn: defaultGlitchTipDSN,
  release: INKHAMMY_WEBSITE_RELEASE,
  environment: location.hostname === 'inkhammy.mindcrafters.group' ? 'production' : 'development',
  sampleRate: 1,
  maxBreadcrumbs: 100,
  sendDefaultPii: false,
  autoSessionTracking: false,
  initialScope: { tags: { component: 'website', 'launch.id': launchID } },
  // The callback wrapper suppresses unrelated rejections during its onerror
  // deduplication window. GlobalHandlers captures both failures independently.
  integrations: (defaults) => defaults.filter((integration) => integration.name !== 'BrowserApiErrors').concat([
    Sentry.captureConsoleIntegration({ levels: ['warn', 'error', 'assert'] }),
    Sentry.httpClientIntegration({ failedRequestStatusCodes: [[400, 599]], failedRequestTargets: [ownOrigin] }),
  ]),
  beforeSend: (event) => (isBrowserExtensionException(event) ? null : sanitizeEvent(event)),
  beforeSendLog: sanitizeLog,
  beforeBreadcrumb: (breadcrumb) => scrubDiagnostic(breadcrumb),
});

// One entry point for errors already handled by the UI (sharing, clipboard, etc.).
window.InkHammyDiagnostics = Object.freeze({
  captureException(error, operation) {
    Sentry.captureException(error, { tags: { operation }, contexts: { page: { path: location.pathname } } });
  },
});

// Cloudflare's edge injects its Web Analytics beacon into HTML responses. The site
// promises no tracking scripts, so the CSP blocks it by design; it is not a site failure.
const isEdgeInjectedAnalytics = (url) => {
  try { return new URL(url, location.href).hostname === 'static.cloudflareinsights.com'; } catch { return false; }
};

// Cloudflare's Google tag gateway injects two inline Google Tag Manager snippets at the top of HTML responses for
// browser user agents. The CSP blocks them by design; the samples are Google's published snippets, never site code.
const edgeInjectedTagSamples = ['(function(w,i,g){w[g]=w[g]||[];', '(function(w,d,s,l){w[l]=w[l]||[];'];
const isEdgeInjectedTag = (event) => event.blockedURI === 'inline' && event.effectiveDirective.startsWith('script-src')
  && edgeInjectedTagSamples.some((prefix) => event.sample.startsWith(prefix));

// What the browser knew about a failed load: the HTTP status Chromium records for
// it (0 when no response arrived), connectivity, and whether automation drove the page.
function resourceLoadContext(href) {
  const timing = performance.getEntriesByName(href).at(-1);
  return {
    'resource.status': timing?.responseStatus ?? 'unavailable',
    'resource.transfer_size': timing?.transferSize ?? 'unavailable',
    'resource.duration_ms': timing ? Math.round(timing.duration) : 'unavailable',
    'navigator.online': navigator.onLine,
    'navigator.webdriver': navigator.webdriver === true,
    'document.ready_state': document.readyState,
    'document.visibility': document.visibilityState,
  };
}

// A same-origin load that got no response at all (an ad or image blocker, data saver, a crawler that skips images,
// or the visitor's network dropping it) has a Chromium timing entry with status 0 and no bytes; blockers can take a
// few milliseconds to cancel it. The site never answered, so it is not a missing file; HTTP failures carry their
// status. Cross-origin timing hides the status of real failures too, so there only a load refused outright (no time
// at all) counts.
const noResponse = (resource, context) => context['resource.status'] === 0 && context['resource.transfer_size'] === 0
  && (resource.origin === location.origin || context['resource.duration_ms'] === 0);

// Resource failures do not become JavaScript exceptions. Preserve the failing URL,
// type and page; group repeats per resource within this page load.
const failedResources = new Set();
addEventListener('error', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const url = target.getAttribute('src') || target.getAttribute('href');
  if (!url) return;
  if (isEdgeInjectedAnalytics(url)) return;
  const resource = new URL(url, location.href);
  const safeURL = `${resource.origin}${resource.pathname}`;
  if (failedResources.has(safeURL)) return;
  failedResources.add(safeURL);
  const context = resourceLoadContext(resource.href);
  if (noResponse(resource, context)) {
    Sentry.addBreadcrumb({ category: 'resource.blocked', level: 'info', message: safeURL,
      data: { 'resource.duration_ms': context['resource.duration_ms'] } });
    return;
  }
  Sentry.captureMessage('website.resource_failed', {
    level: 'error', fingerprint: ['website.resource_failed', safeURL],
    tags: { operation: 'resource.load', 'resource.type': target.tagName.toLowerCase() },
    extra: { resource: safeURL, page: location.pathname, ...context },
  });
}, true);

// Where the violating code came from, without query strings or fragments.
const violationSource = (url) => {
  if (!url) return 'unavailable';
  try {
    const source = new URL(url, location.href);
    return source.protocol.startsWith('http') ? `${source.origin}${source.pathname}` : source.protocol;
  } catch { return 'unparseable'; }
};

addEventListener('securitypolicyviolation', (event) => {
  // A blocked reporting endpoint must not recursively report its own delivery failure.
  if (event.blockedURI.startsWith('https://glitchtip.mindcrafters.group')) return;
  if (isEdgeInjectedAnalytics(event.blockedURI) || isEdgeInjectedTag(event)) return;
  if (isBrowserExtensionSource(event.sourceFile) || isBrowserExtensionSource(event.blockedURI)) return;
  Sentry.captureMessage('website.security_policy_violation', { level: 'warning',
    fingerprint: ['website.security_policy_violation', event.effectiveDirective],
    extra: { directive: event.effectiveDirective, resource: event.blockedURI, page: location.pathname,
      source: violationSource(event.sourceFile), 'source.line': event.lineNumber, 'source.column': event.columnNumber,
      disposition: event.disposition, 'sample.length': event.sample.length,
      // 'report-sample' gives a blocked script's first 40 characters, which name an injected script's origin.
      // Style samples could echo page text, so only scripts carry theirs.
      ...(event.effectiveDirective.startsWith('script-src') ? { sample: event.sample } : {}) } });
});

// UI breadcrumbs describe actions without copying text from controls or fields.
addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('button, a') : null;
  if (!target) return;
  Sentry.addBreadcrumb({ category: 'ui.action', level: 'info', message: target.tagName.toLowerCase(),
    data: { id: target.id || undefined, share: target.hasAttribute('data-share'),
      destination: target.getAttribute('href')?.split(/[?#]/)[0] } });
}, true);

Sentry.logger.info('website.started', { component: 'website', 'launch.id': launchID, 'page.path': location.pathname });
addEventListener('pagehide', () => { void Sentry.flush(2_000); });
